/**
 * Family Store
 *
 * Global state for the multi-family model: the user's families, the ACTIVE
 * family, its members and invitations. Owns the family-switch orchestration:
 * switching updates the server, re-points the shared family context, resets
 * every family-scoped feature store, tears down realtime channels and the
 * service worker's response caches, and syncs the effective role into the
 * auth store.
 *
 * @module features/family/stores/familyStore
 */

import { create } from 'zustand';
import { useAuthStore } from '@/features/auth/stores/authStore';
import type { UserRole } from '@/features/auth/types/auth.types';
import { setActiveFamilyOverride } from '@/shared/lib/familyContext';
import { purgeResponseCaches } from '@/shared/lib/responseCache';
import { resetAllFamilyScopedStores } from '@/shared/lib/storeReset';
import { monitoringService } from '@/shared/services';
import { familyService } from '../services/familyService';
import type {
  CreateInvitationInput,
  Family,
  FamilyInvitation,
  FamilyMember,
  InviteMemberInput,
  InviteMemberResult,
} from '../types/family.types';

interface FamilyState {
  readonly families: readonly Family[];
  readonly activeFamilyId: string | null;
  readonly members: readonly FamilyMember[];
  readonly invitations: readonly FamilyInvitation[];
  /** True once the initial hydration for the current session finished. */
  readonly hydrated: boolean;
  readonly loading: boolean;
  readonly error: string | null;
}

interface FamilyActions {
  /** Load families + active family for the signed-in user. */
  hydrate: () => Promise<void>;
  /** Create a family and make it active. */
  createFamily: (name: string) => Promise<Family>;
  /** Join a family via invitation code (activates it if first family). */
  joinWithCode: (code: string) => Promise<Family>;
  /** Switch the active family and rescope the whole app. */
  switchFamily: (familyId: string) => Promise<void>;
  /** Leave a family. */
  leaveFamily: (familyId: string) => Promise<void>;
  /** Rename the active family (parent only). */
  renameFamily: (name: string) => Promise<void>;
  /** Refresh the members of the active family. */
  refreshMembers: () => Promise<void>;
  /** Refresh the invitations of the active family (parent only). */
  refreshInvitations: () => Promise<void>;
  createInvitation: (input: CreateInvitationInput) => Promise<FamilyInvitation>;
  /** Send a role-scoped email invitation (Edge Function in live mode). */
  inviteMember: (input: InviteMemberInput) => Promise<InviteMemberResult>;
  revokeInvitation: (invitationId: string) => Promise<void>;
  removeMember: (profileId: string) => Promise<void>;
  updateMemberRole: (profileId: string, role: UserRole) => Promise<void>;
  clearError: () => void;
  /** Reset to the pristine state (sign-out). */
  reset: () => void;
}

export type FamilyStore = FamilyState & FamilyActions;

const initialState: FamilyState = {
  families: [],
  activeFamilyId: null,
  members: [],
  invitations: [],
  hydrated: false,
  loading: false,
  error: null,
};

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/** Sync the effective (per-family) role into the auth store. */
function syncRoleForFamily(
  families: readonly Family[],
  familyId: string | null
): void {
  if (familyId === null) {
    return;
  }
  const family = families.find((f) => f.id === familyId);
  if (family) {
    useAuthStore.getState().setUserRole(family.role);
  }
}

/**
 * Tear down everything that is bound to the OUTGOING family scope, so the
 * next one starts clean: the realtime channels, and the service worker's
 * response caches.
 *
 * The caches matter here as much as at logout. `supabase-storage-signed`
 * holds up to 100 private album bodies for 7 days, and the trade-off written
 * out for it in vite.config.ts — a per-device cache on a device that already
 * rendered these photos — is an argument about staying inside ONE scope. A
 * rescope is where it stops holding: the same person, in a family that has
 * not granted them those photos, would otherwise be served them from disk
 * while offline. Renamed from `teardownRealtime` when the cache purge joined
 * it; every rescoping action calls this, not just `switchFamily`.
 *
 * AWAITED BY EVERY CALLER, and that is the point. Fire-and-forget made the
 * teardown a race rather than a step: the line after each call is
 * `setActiveFamilyOverride`, which synchronously re-advertises the new scope
 * to `useRealtimeScope`, so channels re-created for the incoming family could
 * be removed a tick later by the outgoing family's `removeAllChannels()` —
 * with nothing left to trigger another resolve. `useRealtimeScope`'s
 * `FAMILY_SWITCH_SETTLE_MS` delay covers the switch-away-from-a-live-scope
 * case, but only that one: `createFamily` and the first `joinWithCode` start
 * from a null scope, which takes the immediate-resolve branch instead.
 *
 * Never rejects — `Promise.allSettled` over both halves, and
 * `disconnectAllRealtime` is bounded on its own — so awaiting it cannot turn
 * a successful rescope into a failed one.
 */
async function teardownFamilyScope(): Promise<void> {
  const realtime = import('@/shared/clients/supabaseClient').then(
    ({ disconnectAllRealtime }) => disconnectAllRealtime()
  );
  await Promise.allSettled([realtime, purgeResponseCaches()]);
}

export const useFamilyStore = create<FamilyStore>()((set, get) => ({
  ...initialState,

  hydrate: async () => {
    set({ loading: true, error: null });
    try {
      const [families, activeFamilyId] = await Promise.all([
        familyService.getMyFamilies(),
        familyService.getActiveFamilyId(),
      ]);

      // Self-heal: an active id pointing at a family we no longer belong to
      // falls back to the first membership (or none).
      const validActiveId =
        activeFamilyId !== null &&
        families.some((family) => family.id === activeFamilyId)
          ? activeFamilyId
          : (families[0]?.id ?? null);

      if (validActiveId !== null && validActiveId !== activeFamilyId) {
        await familyService.setActiveFamily(validActiveId);
      }

      setActiveFamilyOverride(validActiveId);
      syncRoleForFamily(families, validActiveId);

      set({
        families,
        activeFamilyId: validActiveId,
        hydrated: true,
        loading: false,
      });

      if (validActiveId !== null) {
        await get().refreshMembers();
      }
    } catch (error) {
      set({
        ...initialState,
        hydrated: true,
        error: errorMessage(error, 'Семействата не можаха да бъдат заредени.'),
      });
    }
  },

  createFamily: async (name) => {
    set({ loading: true, error: null });
    try {
      const family = await familyService.createFamily(name);

      await teardownFamilyScope();
      setActiveFamilyOverride(family.id);
      resetAllFamilyScopedStores();
      useAuthStore.getState().setUserRole(family.role);

      set((state) => ({
        families: [...state.families, family],
        activeFamilyId: family.id,
        members: [],
        invitations: [],
        loading: false,
      }));

      await get().refreshMembers();
      monitoringService.trackEvent('Family Created');
      return family;
    } catch (error) {
      set({
        loading: false,
        error: errorMessage(error, 'Семейството не можа да бъде създадено.'),
      });
      throw error;
    }
  },

  joinWithCode: async (code) => {
    set({ loading: true, error: null });
    try {
      const family = await familyService.joinFamilyWithCode(code);
      const families = await familyService.getMyFamilies();
      const activeFamilyId = await familyService.getActiveFamilyId();

      await teardownFamilyScope();
      setActiveFamilyOverride(activeFamilyId);
      resetAllFamilyScopedStores();
      syncRoleForFamily(families, activeFamilyId);

      set({
        families,
        activeFamilyId,
        members: [],
        invitations: [],
        loading: false,
      });

      if (activeFamilyId !== null) {
        await get().refreshMembers();
      }
      monitoringService.trackEvent('Family Joined');
      return family;
    } catch (error) {
      set({
        loading: false,
        error: errorMessage(
          error,
          'Присъединяването към семейството е неуспешно.'
        ),
      });
      throw error;
    }
  },

  switchFamily: async (familyId) => {
    const { activeFamilyId, families } = get();
    if (familyId === activeFamilyId) {
      return;
    }
    set({ loading: true, error: null });
    try {
      await familyService.setActiveFamily(familyId);

      await teardownFamilyScope();
      setActiveFamilyOverride(familyId);
      resetAllFamilyScopedStores();
      syncRoleForFamily(families, familyId);

      set({
        activeFamilyId: familyId,
        members: [],
        invitations: [],
        loading: false,
      });

      await get().refreshMembers();
      monitoringService.trackEvent('Family Switched');
    } catch (error) {
      set({
        loading: false,
        error: errorMessage(error, 'Смяната на семейство е неуспешна.'),
      });
      throw error;
    }
  },

  leaveFamily: async (familyId) => {
    set({ loading: true, error: null });
    try {
      await familyService.leaveFamily(familyId);
      const families = await familyService.getMyFamilies();
      const activeFamilyId = await familyService.getActiveFamilyId();

      await teardownFamilyScope();
      setActiveFamilyOverride(activeFamilyId);
      resetAllFamilyScopedStores();
      syncRoleForFamily(families, activeFamilyId);

      set({
        families,
        activeFamilyId,
        members: [],
        invitations: [],
        loading: false,
      });

      if (activeFamilyId !== null) {
        await get().refreshMembers();
      }
      monitoringService.trackEvent('Family Left');
    } catch (error) {
      set({
        loading: false,
        error: errorMessage(error, 'Напускането на семейството е неуспешно.'),
      });
      throw error;
    }
  },

  renameFamily: async (name) => {
    const { activeFamilyId } = get();
    if (activeFamilyId === null) {
      return;
    }
    set({ loading: true, error: null });
    try {
      await familyService.renameFamily(activeFamilyId, name);
      set((state) => ({
        families: state.families.map((family) =>
          family.id === activeFamilyId
            ? { ...family, name: name.trim() }
            : family
        ),
        loading: false,
      }));
    } catch (error) {
      set({
        loading: false,
        error: errorMessage(
          error,
          'Преименуването на семейството е неуспешно.'
        ),
      });
      throw error;
    }
  },

  refreshMembers: async () => {
    const { activeFamilyId } = get();
    if (activeFamilyId === null) {
      set({ members: [] });
      return;
    }
    try {
      const members = await familyService.getMembers(activeFamilyId);
      set({ members });
    } catch (error) {
      set({
        error: errorMessage(
          error,
          'Членовете на семейството не можаха да бъдат заредени.'
        ),
      });
    }
  },

  refreshInvitations: async () => {
    const { activeFamilyId } = get();
    if (activeFamilyId === null) {
      set({ invitations: [] });
      return;
    }
    try {
      const invitations = await familyService.getInvitations(activeFamilyId);
      set({ invitations });
    } catch (error) {
      set({
        error: errorMessage(error, 'Поканите не можаха да бъдат заредени.'),
      });
    }
  },

  createInvitation: async (input) => {
    const { activeFamilyId } = get();
    if (activeFamilyId === null) {
      throw new Error('Няма активно семейство.');
    }
    try {
      const invitation = await familyService.createInvitation(
        activeFamilyId,
        input
      );
      set((state) => ({ invitations: [invitation, ...state.invitations] }));
      monitoringService.trackEvent('Family Invitation Created');
      return invitation;
    } catch (error) {
      set({
        error: errorMessage(error, 'Поканата не можа да бъде създадена.'),
      });
      throw error;
    }
  },

  inviteMember: async (input) => {
    const { activeFamilyId } = get();
    if (activeFamilyId === null) {
      throw new Error('Няма активно семейство.');
    }
    try {
      const result = await familyService.inviteMember(activeFamilyId, input);
      // The invitation row was created server-side — refresh the list.
      await get().refreshInvitations();
      monitoringService.trackEvent('Family Member Invited', {
        status: result.status,
      });
      return result;
    } catch (error) {
      set({
        error: errorMessage(error, 'Поканата не можа да бъде изпратена.'),
      });
      throw error;
    }
  },

  revokeInvitation: async (invitationId) => {
    try {
      await familyService.revokeInvitation(invitationId);
      set((state) => ({
        invitations: state.invitations.map((invitation) =>
          invitation.id === invitationId
            ? { ...invitation, revokedAt: new Date().toISOString() }
            : invitation
        ),
      }));
    } catch (error) {
      set({
        error: errorMessage(error, 'Поканата не можа да бъде оттеглена.'),
      });
      throw error;
    }
  },

  removeMember: async (profileId) => {
    const { activeFamilyId } = get();
    if (activeFamilyId === null) {
      return;
    }
    try {
      await familyService.removeMember(activeFamilyId, profileId);
      set((state) => ({
        members: state.members.filter(
          (member) => member.profileId !== profileId
        ),
      }));
    } catch (error) {
      set({
        error: errorMessage(error, 'Членът не можа да бъде премахнат.'),
      });
      throw error;
    }
  },

  updateMemberRole: async (profileId, role) => {
    const { activeFamilyId } = get();
    if (activeFamilyId === null) {
      return;
    }
    try {
      await familyService.updateMemberRole(activeFamilyId, profileId, role);
      set((state) => ({
        members: state.members.map((member) =>
          member.profileId === profileId ? { ...member, role } : member
        ),
      }));
    } catch (error) {
      set({
        error: errorMessage(error, 'Ролята не можа да бъде променена.'),
      });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set(initialState);
  },
}));

// Reset the family scope automatically when the user signs out. Subscribing
// here (family → auth) keeps the import direction acyclic — the auth store
// never needs to know about this feature.
useAuthStore.subscribe((state, prevState) => {
  if (prevState.isAuthenticated && !state.isAuthenticated) {
    useFamilyStore.getState().reset();
  }
});
